import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { lastValueFrom } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { CourseState } from 'src/app/components/members/courses/courses.reducer';
import { getCourseSelected } from 'src/app/components/members/courses/courses.selector';
import { ChapterSection } from 'src/app/models/chapter-sections.model';
import { Chapter } from 'src/app/models/chapters.model';
import { environment } from 'src/environments/environment';


interface CourseChapterSections {
  courseId: number;
  chapterId: number;
  sections: ChapterSection[];
}

export interface PagedSections {
  /** Page of section headers plus all of their subsections. */
  data: ChapterSection[];
  /** Total number of top-level headers for the chapter (paginator pages by headers). */
  totalRecords: number;
  /** Lightweight list of every header in the chapter (for ordering/parent dropdowns). */
  headers: ChapterSection[];
  /** Lightweight list of every section (headers + subsections) in the chapter, for the "After" dropdown/reorder so it isn't limited to the current page. */
  allSections: { id: number; name: string; order: number; parentId: number }[];
}

export interface SectionLoadParams {
  first?: number;
  rows: number;
  sortField?: string;
  sortOrder?: number;
  filters?: any;
}

@Injectable({
  providedIn: 'root'
})
export class ChapterSectionsService {
  baseURL: string = environment.apiUrl;
  courseChapterSections: CourseChapterSections[] = [];

  constructor(private http: HttpClient, private courseStore: Store<CourseState>) {
    // Refresh cached sections whenever the selected course changes, so a newly
    // selected course always fetches its own (fresh) sections.
    this.courseStore
      .pipe(
        select(getCourseSelected),
        map(course => (course ? course.id : null)),
        distinctUntilChanged()
      )
      .subscribe(() => this.clearCache());
  }

  /** Drop all cached course/chapter sections. */
  clearCache() {
    this.courseChapterSections = [];
  }

  deleteCourseChapterSections(courseId: number, chapterId: number) {
    // Remove the cache entry entirely so the next findAll re-fetches. Merely
    // emptying `sections` left the entry present, causing findAll's existence
    // check to return an empty array instead of refetching after an edit.
    this.courseChapterSections = this.courseChapterSections.filter(
      c => !(c.courseId === courseId && c.chapterId === chapterId)
    );
  }

  async findAll(courseId: number, chapterId: number) {
    if (this.courseChapterSections.find(c => c.courseId === courseId && c.chapterId === chapterId)) {
      return this.courseChapterSections.find(c => c.courseId === courseId && c.chapterId === chapterId)?.sections;
    }

    const sections = await lastValueFrom(
      this.http
        .get<Array<ChapterSection>>(`${environment.apiUrl}/courses/${courseId}/chapters/${chapterId}/sections`)
    );

    this.courseChapterSections.push({ courseId, chapterId, sections });
    return sections;
  }

  /**
   * Lazy/paginated fetch for the admin editor: returns one page of headers (with
   * their subsections) plus the lightweight full header list. Not cached, since
   * the page varies with paging/sort/filter.
   */
  async findAllPaged(courseId: number, chapterId: number, params: SectionLoadParams): Promise<PagedSections> {
    let httpParams: any = { rows: `${params.rows}` };
    if (params.first != null) httpParams.first = `${params.first}`;
    if (params.sortField) httpParams.sortField = params.sortField;
    if (params.sortOrder != null) httpParams.sortOrder = `${params.sortOrder}`;
    if (params.filters) httpParams.filters = encodeURIComponent(JSON.stringify(params.filters));

    return await lastValueFrom(
      this.http.get<PagedSections>(
        `${environment.apiUrl}/courses/${courseId}/chapters/${chapterId}/sections`,
        { params: httpParams }
      )
    );
  }

  /** Persist ordering only (order + parentId) without touching content/name. */
  async reorder(courseId: number, chapterId: number, sections: { id: number; order: number; parentId: number }[]) {
    return await lastValueFrom(
      this.http.put(
        `${environment.apiUrl}/courses/${courseId}/chapters/${chapterId}/sections/reorder`,
        sections
      )
    );
  }

  async findBy(courseId: number, chapterId: number, sectionId: number) {
    if (this.courseChapterSections.find(c => c.courseId === courseId && c.chapterId === chapterId)) {
      return this.courseChapterSections.find(c => c.courseId === courseId && c.chapterId === chapterId)?.sections.find(s => s.id === sectionId);
    }

    const sections = await this.findAll(courseId, chapterId);
    return sections.find(s => s.id === sectionId);
  }

  async update(courseId: number, chapterId: number, sectionId: number, section: ChapterSection) {
    this.deleteCourseChapterSections(courseId, chapterId);
    return await lastValueFrom(
      this.http
        .put<ChapterSection>(`${environment.apiUrl}/courses/${courseId}/chapters/${chapterId}/sections/${sectionId}`, section)
    );
  }

  async updateSections(courseId: number, chapterId: number, sections: ChapterSection[]) {
    this.deleteCourseChapterSections(courseId, chapterId);
    return await lastValueFrom(
      this.http
        .put<ChapterSection>(`${environment.apiUrl}/courses/${courseId}/chapters/${chapterId}/sections`, sections)
    );
  }

  async create(courseId: number, chapterId: number, section: ChapterSection): Promise<ChapterSection> {
    this.deleteCourseChapterSections(courseId, chapterId);
    return await lastValueFrom(
      this.http
        .post<ChapterSection>(`${environment.apiUrl}/courses/${courseId}/chapters/${chapterId}/sections`, section)
    );
  }

  async delete(courseId: number, chapterId: number, sectionId: number) {
    this.deleteCourseChapterSections(courseId, chapterId);
    return await lastValueFrom(
      this.http
        .delete(`${environment.apiUrl}/courses/${courseId}/chapters/${chapterId}/sections/${sectionId}`)
    );
  }
}
